/**
 * The table's own controls.
 *
 * Administration is the Owner's, facts about the fiction are the DM's with the
 * engine validating each one against the printed tables, and everything else is
 * the engine's or the player's it belongs to.
 *
 * ⚠ **A discretionary experience award, converting a studied manual, offering a
 * henchman a place of their own and raising a stronghold are the *table's***
 * (`player:propose`, decided by a majority of those present) — and their
 * controls live on `CampaignPage`, not here. Buttons here once emitted events
 * no handler listened for, and nothing caught it: `onCommand` takes a bare
 * string, so the contract cannot typecheck what is sent through it.
 *
 * What is left is three things that are nobody's judgement:
 *
 *  - **advance the round** — a nudge, and the window closes itself anyway;
 *  - **a presence policy** — what happens on a seat while its player is away,
 *    which was never adjudication and sat behind the role only because every
 *    `host:*` event did;
 *  - **the roster**, so the two above have something to act on.
 *
 * Fog reveal is not here: it is the Owner's, on the map panel, because what a
 * table has seen of its own map is administration.
 */

import {
  AWAY_POLICIES,
  AWAY_POLICY_HELP,
  AWAY_POLICY_LABELS,
  USE_TABLE_SETTING,
} from './awayPolicy.js';
import type { CombatView } from '../realtime/combat.js';
import type { RosterEntry } from '../realtime/socket.js';

export interface TablePanelProps {
  /**
   * The seats this viewer may actually direct — their own and their retainers'.
   *
   * ⚠ **Narrowed by the caller, not shown whole.** `host:set_presence_policy`
   * is gated on `actingCharacter` — your own or a retainer of yours — so a list
   * of everybody would be a column of controls whose only outcome is a refusal.
   * That is the same fault as offering a character who is already seated at
   * another table.
   */
  readonly roster: readonly RosterEntry[];
  readonly combat: CombatView | null;
  readonly onCommand: (event: string, payload?: Record<string, unknown>) => void;
}

/**
 * What happens on a seat while its player is away.
 *
 * ⚠ **There were three, and the third promised a human would play the seat.**
 * It did something worse than either honest answer: the round waited for a
 * person who was never coming, the 60-second window expired, and the sweeper
 * passed the turn four minutes later — auto-pass with a five-minute delay,
 * under a label promising otherwise. It *is* auto-pass, and migration 0024
 * rewrites the stored ones.
 */

export function TablePanel({ roster, combat, onCommand }: TablePanelProps) {
  return (
    <div className="space-y-3 text-sm">
      {/* ⚠ **There is no "start combat" control, and no "end combat" one
          either.** A fight begins from a player's attack or from the engine
          deciding an NPC swings, and it ends four printed ways — three the
          engine answers and one the DM does. Neither is a button. */}

      {combat && (
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => onCommand('host:advance')}
            // **Says it is optional, or the burden is not actually removed.**
            // The window closes itself once the grace runs out; somebody who
            // does not know that presses this every round, which is the cost
            // the sweeper exists to end.
            title="close the declaration window early, passing anyone who has not declared — it closes on its own a few minutes after the countdown either way"
            className="rounded-md border border-neutral-300 px-3 py-1 dark:border-neutral-700"
          >
            Advance the round
          </button>
        </div>
      )}

      <div className="space-y-1">
        <p className="text-xs text-neutral-500 dark:text-neutral-400">
          If a player is away, their character…
        </p>
        <details className="text-xs text-neutral-500 dark:text-neutral-400">
          <summary className="cursor-pointer" title={AWAY_POLICY_HELP}>
            What do these mean?
          </summary>
          <p className="mt-1">{AWAY_POLICY_HELP}</p>
        </details>
        <ul className="space-y-1">
          {roster.map((player) => (
            // **A block per player, not a wrapping row.** As one flex line the
            // name, the policy and the control reflowed differently for every
            // name length: one player's sat beside them, the next player's
            // below, the third's split across two lines. The layout must not
            // depend on how long a name is.
            <li
              key={player.characterId}
              className="space-y-1 rounded-md border border-neutral-200 p-2 dark:border-neutral-800"
            >
              <span className="block font-medium">{player.name}</span>
              <select
                aria-label={`While ${player.name} is away`}
                value={player.presencePolicy ?? ''}
                onChange={(e) =>
                  onCommand('host:set_presence_policy', {
                    characterId: player.characterId,
                    policy: e.target.value,
                  })
                }
                className="w-full rounded-md border border-neutral-300 bg-white px-1 py-0.5 text-xs dark:border-neutral-700 dark:bg-neutral-900"
              >
                {/* Empty means the campaign default, which is a different
                    thing from any of the three and stays distinguishable. */}
                <option value="">{USE_TABLE_SETTING}</option>
                {AWAY_POLICIES.map((policy) => (
                  <option key={policy} value={policy}>
                    {AWAY_POLICY_LABELS[policy]}
                  </option>
                ))}
              </select>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
